import {
  Box,
  Button,
  type ButtonProps,
  createTheme,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ThemeProvider,
  Typography,
} from '@mui/material';
import moment, { type Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import {
  MdOutlineKeyboardArrowLeft,
  MdOutlineKeyboardArrowRight,
} from 'react-icons/md';

import CalendarSvg from './svg/CalendarSvg';
import type DatePickerProps from './types';

const theme = createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
        },
        outlined: {
          borderColor: '#8B8B8B',
          color: '#8B8B8B',
          '&:hover': {
            color: '#8B8B8B',
          },
        },
      },
    },
  },
});

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  pickerType,
  disabled,
  minDate = moment().subtract(20, 'years').startOf('year'),
  maxDate = moment(),
  disabledTimes,
}) => {
  const [open, setOpen] = useState(false);
  const [showConfig, setShowConfig] = useState({
    picker: ['year', 'month', 'day'],
    label: (time: Moment) => time.format('YYYY-MM-DD'),
  });
  const [selectedDate, setSelectedDate] = useState(moment(value));
  const [pickerDate, setPickerDate] = useState(moment(value));
  const [currentMonthDates, setCurrentMonthDates] = useState<(Moment | null)[]>(
    []
  );
  const [currentPicker, setCurrentPicker] = useState<'year' | 'month' | 'day'>(
    'day'
  );

  const arrowButtonBehavior = {
    day: {
      left: {
        disabled: pickerDate.isSame(minDate, 'month'),
        onClick: () =>
          setPickerDate((prev) => moment(prev).subtract(1, 'month')),
      },
      right: {
        disabled: pickerDate.isSame(maxDate, 'month'),
        onClick: () => setPickerDate((prev) => moment(prev).add(1, 'month')),
      },
    },
    month: {
      left: {
        disabled: pickerDate.isSame(minDate, 'year'),
        onClick: () =>
          setPickerDate((prev) => moment(prev).subtract(1, 'year')),
      },
      right: {
        disabled: pickerDate.isSame(maxDate, 'year'),
        onClick: () => setPickerDate((prev) => moment(prev).add(1, 'year')),
      },
    },
    year: {
      left: {
        disabled: true,
        onClick: () => {},
      },
      right: {
        disabled: true,
        onClick: () => {},
      },
    },
  };

  useEffect(() => {
    if (open) {
      setPickerDate(selectedDate);
    }
  }, [open, selectedDate]);

  useEffect(() => {
    onChange(selectedDate);
  }, [onChange, selectedDate]);

  useEffect(() => {
    if (pickerType === 'week') {
      setCurrentPicker('day');
    } else {
      setCurrentPicker(pickerType);
    }
  }, [pickerType]);

  useEffect(() => {
    const newMonthDates: (Moment | null)[] = new Array(42).fill(null);
    const firstDay = moment(pickerDate).startOf('month');
    const lastDay = moment(pickerDate).endOf('month');
    const firstDayIndex = firstDay.weekday();
    const lastDayDate = lastDay.date();
    for (let i = firstDayIndex; i < firstDayIndex + lastDayDate; i++) {
      newMonthDates[i] = moment(firstDay).add(i - firstDayIndex, 'day');
    }
    setCurrentMonthDates(newMonthDates);
  }, [pickerDate]);

  useEffect(() => {
    setShowConfig(() => {
      switch (pickerType) {
        case 'week':
          return {
            picker: ['year', 'month', 'day'],
            label: (time) =>
              `${moment(time).startOf('week').format('YY-MM-DD')}~
              ${moment(time).endOf('week').format('YY-MM-DD')}`,
          };
        case 'month':
          return {
            picker: ['year', 'month'],
            label: (time) => time.format('YYYY-MM'),
          };
        case 'year':
          return {
            picker: ['year'],
            label: (time) => time.format('YYYY'),
          };
        default:
          return {
            picker: ['year', 'month', 'day'],
            label: (time) => time.format('YYYY-MM-DD'),
          };
      }
    });
  }, [pickerType]);

  return (
    <ThemeProvider theme={theme}>
      <Button
        variant="outlined"
        disableRipple
        onClick={() => setOpen(true)}
        disabled={disabled}
        endIcon={<CalendarSvg />}
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          paddingRight: '1rem',
          paddingLeft: '1rem',
          height: '3.1rem',
        }}
      >
        {showConfig.label(selectedDate)}
      </Button>
      <Dialog
        sx={{
          '& .MuiDialog-paper': {
            width: 'unset',
            maxWidth: 'unset',
          },
        }}
        open={open}
        onClose={() => setOpen(false)}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            {pickerType !== 'year' && (
              <Button onClick={() => setCurrentPicker('year')}>
                {pickerDate.format('YYYY年')}
              </Button>
            )}
            {(pickerType === 'day' || pickerType === 'week') && (
              <Button onClick={() => setCurrentPicker('month')}>
                {pickerDate.format('MM月')}
              </Button>
            )}
          </Box>
          {pickerType !== 'year' && (
            <Box>
              <IconButton
                disabled={arrowButtonBehavior[currentPicker].left.disabled}
                onClick={arrowButtonBehavior[currentPicker].left.onClick}
              >
                <MdOutlineKeyboardArrowLeft />
              </IconButton>
              <IconButton
                disabled={arrowButtonBehavior[currentPicker].right.disabled}
                onClick={arrowButtonBehavior[currentPicker].right.onClick}
              >
                <MdOutlineKeyboardArrowRight />
              </IconButton>
            </Box>
          )}
        </DialogTitle>
        <DialogContent
          sx={{
            width: '23rem',
            height: '18rem',
            maxWidth: '100%',
          }}
        >
          {currentPicker === 'day' && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gridTemplateRows: 'repeat(7, 1fr)',
                alignItems: 'center',
                justifyItems: 'center',
              }}
            >
              {new Array(7).fill('').map((_, i) => (
                <Typography key={`weekDay${i}`}>
                  {moment().weekday(i).format('ddd')}
                </Typography>
              ))}
              {/* TODO: 把產生年月日選項整合成一個function */}
              {currentMonthDates.map((date, i) => {
                let variant: 'text' | 'contained' = 'text';
                const sx = {
                  width: '2rem',
                  height: '2rem',
                  minWidth: 'unset',
                  borderRadius: '50%',
                  color: '#383838',
                };
                if (
                  date &&
                  moment(selectedDate)
                    .startOf('day')
                    .isSame(moment(date).startOf('day'))
                ) {
                  variant = 'contained';
                  sx.color = '#fff';
                }
                if (
                  pickerType === 'week' &&
                  date &&
                  moment(selectedDate)
                    .startOf('week')
                    .isSame(moment(date).startOf('week'))
                ) {
                  variant = 'contained';
                }
                if (date) {
                  return (
                    <Button
                      key={`date${i}`}
                      variant={variant}
                      disabled={
                        moment(date).endOf('day') < minDate ||
                        moment(date).startOf('day') > maxDate ||
                        (disabledTimes ? disabledTimes(date, 'day') : false)
                      }
                      onClick={() => {
                        setSelectedDate(date);
                        setTimeout(() => setOpen(false), 500);
                      }}
                      sx={sx}
                    >
                      {date.format('D')}
                    </Button>
                  );
                } else {
                  return <Box key={`date${i}`} />;
                }
              })}
            </Box>
          )}
          {currentPicker === 'month' && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gridTemplateRows: 'repeat(3, 1fr)',
                alignItems: 'center',
                justifyItems: 'center',
              }}
            >
              {new Array(12)
                .fill('')
                .map((_, i) => moment(pickerDate).month(i))
                .map((month) => {
                  let variant: 'text' | 'contained' = 'text';
                  let sx: ButtonProps['sx'] = {
                    height: '2rem',
                  };
                  if (
                    moment(month)
                      .startOf('month')
                      .isSame(moment(selectedDate).startOf('month'))
                  ) {
                    variant = 'contained';
                    sx = {
                      ...sx,
                      color: '#fff',
                    };
                  }
                  return (
                    <Button
                      key={month.valueOf()}
                      variant={variant}
                      sx={sx}
                      disabled={
                        moment(month).endOf('month') < minDate ||
                        moment(month).startOf('month') > maxDate ||
                        (disabledTimes ? disabledTimes(month, 'month') : false)
                      }
                      onClick={() => {
                        if (showConfig.picker.includes('day')) {
                          setCurrentPicker('day');
                          setPickerDate(month);
                        } else {
                          setSelectedDate(month);
                          setTimeout(() => setOpen(false), 500);
                        }
                      }}
                    >
                      {month.format('MMM')}
                    </Button>
                  );
                })}
            </Box>
          )}
          {currentPicker === 'year' && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                overflow: 'auto',
              }}
            >
              {new Array(maxDate.year() - minDate.year() + 1)
                .fill('')
                .map((_, i) => moment().subtract(i, 'year'))
                .map((year) => {
                  let variant: 'text' | 'contained' = 'text';
                  let sx: ButtonProps['sx'] = {
                    height: '2rem',
                  };
                  if (
                    moment(year)
                      .startOf('year')
                      .isSame(moment(selectedDate).startOf('year'))
                  ) {
                    variant = 'contained';
                    sx = {
                      ...sx,
                      color: '#fff',
                    };
                  }
                  return (
                    <Button
                      key={year.valueOf()}
                      variant={variant}
                      sx={sx}
                      disabled={
                        moment(year).endOf('year') < minDate ||
                        moment(year).startOf('year') > maxDate ||
                        (disabledTimes ? disabledTimes(year, 'year') : false)
                      }
                      onClick={() => {
                        if (showConfig.picker.includes('month')) {
                          setCurrentPicker('month');
                          setPickerDate(year);
                        } else {
                          setSelectedDate(year);
                          setTimeout(() => setOpen(false), 500);
                        }
                      }}
                    >
                      {year.format('YYYY')}
                    </Button>
                  );
                })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
};

export default DatePicker;
